import React from "react"
import { Toggle } from "../Toggle"
import { SIGNUP_URL } from "../../constants/urls"

// Non Typed External Libs
const Cookies = require("js-cookie")

interface PricingCalculatorProps {
  numberOfPeople: number,
  defaultItemsPerOrder: number,
  plans: Plan[],
}

interface PricingCalculatorState {
  itemsPerOrder: number,
  plan: Plan,
}

interface Plan {
  id: number,
  planOptions: PlanOption[],
}

interface PlanOption {
  itemsPerOrderOptions: number[],
  tiers: PricingTier[],
}

interface PricingTier {
  min: number,
  max: number,
  pricePerUnit: number,
}

class PricingCalculatorInner extends
  React.Component<PricingCalculatorProps, PricingCalculatorState> {
  static defaultProps: PricingCalculatorProps = {
    numberOfPeople: 2,
    defaultItemsPerOrder: 3,
    plans: [{
      id: 38,
      planOptions: [{
        itemsPerOrderOptions: [3, 4, 5],
        tiers: [{ min: 0, max: 999, pricePerUnit: 11.99 }],
      }],
    }],
  }

  constructor(props: PricingCalculatorProps) {
    super(props)
    const { defaultItemsPerOrder } = this.props

    this.state = {
      itemsPerOrder: defaultItemsPerOrder,
      plan: this.findPlan(),
    }

    this.renderPerWeek = this.renderPerWeek.bind(this)
    this.renderPerServing = this.renderPerServing.bind(this)
    this.selectItemsPerOrder = this.selectItemsPerOrder.bind(this)
    this.formatTotal = this.formatTotal.bind(this)
    this.ariaSubmitLabel = this.ariaSubmitLabel.bind(this)
    this.renderCTA = this.renderCTA.bind(this)
  }

  findPlan(): Plan {
    const { plans } = this.props
    const planId = parseInt(Cookies.get("plan_id"), 10)
    const plan = plans.find((plan) => plan.id === planId)

    if (plan) {
      return plan
    }
    return plans[0]
  }

  selectItemsPerOrder(itemsPerOrder: number) {
    this.setState({ itemsPerOrder })
  }

  calculateTotal() {
    const { numberOfPeople } = this.props
    const { itemsPerOrder } = this.state
    const tier = this.findPriceTier()
    return itemsPerOrder * numberOfPeople * tier.pricePerUnit
  }

  findPriceTier(): PricingTier {
    const { numberOfPeople } = this.props
    const { plan, itemsPerOrder } = this.state
    const servings = numberOfPeople * itemsPerOrder
    const planOption = plan.planOptions[0]
    return planOption.tiers.find((tier) => tier.min <= servings && tier.max > servings)
  }

  toggleOptions() {
    const { plan } = this.state
    const { planOptions } = plan
    const { itemsPerOrderOptions } = planOptions[0]
    return itemsPerOrderOptions.map((option) => ({ value: option, label: option }))
  }

  formatTotal() {
    const total = this.calculateTotal()
    return `$${total.toFixed(2)}`
  }

  ariaSubmitLabel() {
    const total = this.formatTotal()
    return `Select this plan for ${total}`
  }

  renderPerServing() {
    const pricingTier = this.findPriceTier()

    return (
      <div className="flex flex-row text-left justify-between py-2 border-solid border-t border-b border-gray-medium">
        <div>
          <strong className="block text-base sm:text-lg/6">
            $
            {pricingTier.pricePerUnit}
            {" "}
            per serving
          </strong>
        </div>
        <div className="text-right">
          <strong className="block text-base sm:text-lg/6">{this.formatTotal()}</strong>
          Total
        </div>
      </div>
    )
  }

  renderEmptyState() {
    return (
      <div className="text-center py-2 border-solid border-t border-b border-gray-medium">
        Select an option below
      </div>
    )
  }

  renderPerWeek() {
    const { itemsPerOrder } = this.state
    const { numberOfPeople } = this.props

    return (
      <div className="flex flex-row justify-between pb-6 gap-2.5">
        <div className="w-[47%]">
          <strong className="block text-base sm:text-lg/6">Nights per week</strong>
          (
          {numberOfPeople}
          {" "}
          servings ea.)
        </div>
        <div className="pricing__toggle w-[47%] max-w-[250px]" role="listbox">
          <Toggle
            onSelect={this.selectItemsPerOrder}
            options={this.toggleOptions()}
            selected={itemsPerOrder}
          />
        </div>
      </div>
    )
  }

  renderCTA() {
    return (
      <div className="text-center mt-6">
        <a
          href={SIGNUP_URL}
          className="inline-block text-center font-bold rounded-full w-full
            no-underline transition-all duration-200 ease-in-out
            active:[box-shadow:0px_4px_4px_0px_rgba(0,0,0,0.25)_inset]
            hover:no-underline text-xs/4 border border-transparent
            px-4 py-3 bg-black text-white focus:bg-black-secondary
            hover:bg-black-secondary active:bg-gray-dark"
          aria-label={this.ariaSubmitLabel()}
        >
          Select This Plan
        </a>
      </div>
    )
  }

  render() {
    const { numberOfPeople } = this.props
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200 border-solid text-black-secondary text-base/5 w-full shadow-[0_4px_8px_0px_rgba(0,0,0,0.2)]">
        <h3 className="text-[28px]/8 font-bold text-center pb-6">
          Dinner for
          {" "}
          {numberOfPeople}
        </h3>
        {this.renderPerWeek()}
        {this.renderPerServing()}
        {this.renderCTA()}
      </div>
    )
  }
}

export function PricingCalculator(props: PricingCalculatorProps) {
  return <PricingCalculatorInner {...props} />
}
